import {
  canonicalNikkeId,
  fallbackNikkeIdentifier,
  nikkeIdentifierCandidates,
} from '../../nikke-catalog/domain/nikkeCatalog.js';

let defaultPracticeNikkeCatalog = null;

export function setPracticeMatcherCatalog(catalog) {
  defaultPracticeNikkeCatalog = catalog || null;
}

function numberValue(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function textValue(value) {
  return String(value ?? '').trim();
}

function rawDeckCode(item) {
  if (typeof item === 'string' || typeof item === 'number') return textValue(item);
  return textValue(item?.code ?? item?.nikke_id ?? item?.nikkeId ?? item?.id ?? item?.name);
}

export function practiceDeckCodes(deck = [], catalog = null) {
  return (Array.isArray(deck) ? deck : [])
    .map((item) => {
      const canonical = catalog ? canonicalNikkeId(catalog, item) : '';
      return canonical || rawDeckCode(item);
    })
    .filter(Boolean)
    .slice(0, 5);
}

export function canonicalPracticeDeckCode(value, catalog = defaultPracticeNikkeCatalog) {
  const resolved = catalog ? canonicalNikkeId(catalog, value) : '';
  if (resolved) return resolved;
  for (const candidate of nikkeIdentifierCandidates(value)) {
    const fallback = fallbackNikkeIdentifier(candidate);
    if (fallback) return fallback;
  }
  return '';
}

export function practiceDeckSignature(deck = [], catalog = defaultPracticeNikkeCatalog) {
  const codes = (Array.isArray(deck) ? deck : [])
    .map((code) => canonicalPracticeDeckCode(code, catalog))
    .filter(Boolean)
    .slice(0, 5);
  return codes.length ? codes.map((code) => canonicalPracticeDeckCode(code, catalog)).join('|') : '';
}

export function completePracticeDeckSignature(deck = [], catalog = defaultPracticeNikkeCatalog) {
  const codes = (Array.isArray(deck) ? deck : [])
    .map((code) => canonicalPracticeDeckCode(code, catalog))
    .filter(Boolean)
    .slice(0, 5);
  return codes.length === 5 ? codes.sort().join('|') : '';
}

function manualId(record, index) {
  return textValue(record?.id) || `manual-${numberValue(record?.slot_no ?? record?.slotNo, index + 1)}`;
}

function actualId(record, index) {
  return textValue(record?.record_key ?? record?.recordKey ?? record?.id) || `actual-${index + 1}`;
}

function normalizeManual(record, index, catalog) {
  return {
    ...record,
    id: manualId(record, index),
    slotNo: numberValue(record?.slot_no ?? record?.slotNo, index + 1),
    bossNo: numberValue(record?.boss_no ?? record?.bossNo),
    damageEok: numberValue(record?.damage_eok ?? record?.damageEok ?? record?.damage),
    deck: practiceDeckCodes(record?.deck ?? record?.deck_codes ?? record?.deckCodes, catalog),
    title: textValue(record?.title),
    note: textValue(record?.note),
    used: Boolean(record?.used),
  };
}

function normalizeActual(record, index, catalog) {
  return {
    ...record,
    id: actualId(record, index),
    attackNo: numberValue(record?.attack_no ?? record?.attackNo, index + 1),
    bossNo: numberValue(record?.boss_no ?? record?.bossNo),
    phaseNo: numberValue(record?.phase_no ?? record?.phaseNo),
    damageEok: numberValue(record?.damage_eok ?? record?.damageEok),
    deck: practiceDeckCodes(record?.deck, catalog),
    isKill: Boolean(record?.is_kill ?? record?.isKill),
    attackedAt: record?.attacked_at ?? record?.attackedAt ?? null,
  };
}

function makeMerged(actual, manual, actualIndex) {
  const actualDamage = actual?.damageEok ?? null;
  const plannedDamage = manual?.damageEok ?? null;
  return {
    key: actual ? `actual:${actual.id}` : `manual:${manual.id}`,
    source: actual && manual ? 'real_applied' : actual ? 'real_only' : 'manual',
    manual: manual || null,
    actual: actual || null,
    slotNo: manual?.slotNo ?? actualIndex + 1,
    phaseNo: actual?.phaseNo ?? null,
    actualPhaseNo: actual?.phaseNo ?? null,
    plannedDamageEok: plannedDamage,
    actualDamageEok: actualDamage,
    displayDamageEok: actualDamage ?? plannedDamage ?? 0,
    deltaEok: actualDamage !== null && plannedDamage !== null ? actualDamage - plannedDamage : null,
    displayDeck: actual?.deck?.length ? actual.deck : manual?.deck || [],
    used: Boolean(actual) || Boolean(manual?.used),
  };
}

function realAppliedSortPriority(entry) {
  return entry?.source === 'real_applied' ? 0 : 1;
}

function sortMergedEntries(entries) {
  return entries.sort((a, b) => realAppliedSortPriority(a) - realAppliedSortPriority(b)
    || a.slotNo - b.slotNo
    || Number(a.source === 'manual') - Number(b.source === 'manual')
    || String(a.key).localeCompare(String(b.key)));
}

export function mergePracticeWithActual({ manualRecords = [], actualRecords = [], bossNo = null, catalog = defaultPracticeNikkeCatalog } = {}) {
  const normalizedBossNo = bossNo === null ? null : numberValue(bossNo);
  const manuals = (Array.isArray(manualRecords) ? manualRecords : [])
    .map((record, index) => normalizeManual(record, index, catalog))
    .filter((record) => !normalizedBossNo || record.bossNo === normalizedBossNo)
    .sort((a, b) => a.slotNo - b.slotNo);
  const actuals = (Array.isArray(actualRecords) ? actualRecords : [])
    .map((record, index) => normalizeActual(record, index, catalog))
    .filter((record) => !normalizedBossNo || record.bossNo === normalizedBossNo)
    .sort((a, b) => a.attackNo - b.attackNo || String(a.attackedAt || '').localeCompare(String(b.attackedAt || '')));

  const usedManualIds = new Set();
  const output = [];

  actuals.forEach((actual, actualIndex) => {
    const actualSignature = completePracticeDeckSignature(actual.deck, catalog);
    const matched = actualSignature
      ? manuals.find((manual) => !usedManualIds.has(manual.id)
        && completePracticeDeckSignature(manual.deck, catalog) === actualSignature) || null
      : null;

    if (matched) usedManualIds.add(matched.id);
    output.push(makeMerged(actual, matched, actualIndex));
  });

  manuals.forEach((manual) => {
    if (!usedManualIds.has(manual.id)) output.push(makeMerged(null, manual, output.length));
  });

  return sortMergedEntries(output);
}

/* Admin edits must never target a different hidden mock record. */
export function mergePracticeWithActualForAdmin(options = {}) {
  return mergePracticeWithActual(options);
}

/*
 * Before all three real attacks arrive, keep the normal five-boss board.
 * After all three arrive, show one column per real attack. This preserves
 * duplicate-boss attacks as separate cards while removing un-attacked bosses.
 */
export function adminPracticeDisplayGroups({ bosses = [], bossEntries = new Map(), actualComplete = false } = {}) {
  const bossList = Array.isArray(bosses) ? bosses : [];
  const bossByNo = new Map(bossList.map((boss) => [numberValue(boss?.bossNo), boss]));

  if (!actualComplete) {
    return bossList.map((boss) => ({
      key: `boss:${numberValue(boss?.bossNo)}`,
      boss,
      entries: bossEntries instanceof Map ? bossEntries.get(numberValue(boss?.bossNo)) || [] : [],
      attackNo: null,
    }));
  }

  const attacks = [];
  if (bossEntries instanceof Map) {
    for (const [bossNo, entries] of bossEntries.entries()) {
      for (const entry of Array.isArray(entries) ? entries : []) {
        if (!entry?.actual) continue;
        const normalizedBossNo = numberValue(entry.actual?.bossNo ?? bossNo);
        attacks.push({
          key: `attack:${entry.actual.id}`,
          boss: bossByNo.get(normalizedBossNo) || { bossNo: normalizedBossNo, bossName: `보스 ${normalizedBossNo}`, element: '' },
          entries: [entry],
          attackNo: numberValue(entry.actual?.attackNo, entry.slotNo),
        });
      }
    }
  }

  return attacks.sort((a, b) => a.attackNo - b.attackNo || String(a.key).localeCompare(String(b.key)));
}

export function buildPracticePhaseSummaries({ phases = [], actualRecords = [], bossNo = null } = {}) {
  const normalizedBossNo = bossNo === null ? null : numberValue(bossNo);
  const phaseMap = new Map();
  for (const phase of Array.isArray(phases) ? phases : []) {
    const phaseBossNo = numberValue(phase?.boss_no ?? phase?.bossNo);
    if (normalizedBossNo && phaseBossNo !== normalizedBossNo) continue;
    const phaseNo = numberValue(phase?.phase_no ?? phase?.phaseNo);
    if (phaseNo < 1 || phaseNo > 4) continue;
    phaseMap.set(phaseNo, {
      phaseNo,
      maxHpEok: phase?.max_hp_eok === null || phase?.maxHpEok === null
        ? null
        : numberValue(phase?.max_hp_eok ?? phase?.maxHpEok),
      isInfinite: Boolean(phase?.is_infinite ?? phase?.isInfinite) || phaseNo === 4,
      actualDamageEok: 0,
      attackCount: 0,
      remainingHpEok: null,
      progressPercent: 0,
    });
  }

  for (let phaseNo = 1; phaseNo <= 4; phaseNo += 1) {
    if (!phaseMap.has(phaseNo)) phaseMap.set(phaseNo, {
      phaseNo,
      maxHpEok: phaseNo === 4 ? null : 0,
      isInfinite: phaseNo === 4,
      actualDamageEok: 0,
      attackCount: 0,
      remainingHpEok: phaseNo === 4 ? null : 0,
      progressPercent: 0,
    });
  }

  for (const raw of Array.isArray(actualRecords) ? actualRecords : []) {
    const actual = normalizeActual(raw, 0);
    if (normalizedBossNo && actual.bossNo !== normalizedBossNo) continue;
    const summary = phaseMap.get(actual.phaseNo);
    if (!summary) continue;
    summary.actualDamageEok += actual.damageEok;
    summary.attackCount += 1;
  }

  return [...phaseMap.values()].sort((a, b) => a.phaseNo - b.phaseNo).map((summary) => {
    if (summary.isInfinite) return { ...summary, remainingHpEok: null, progressPercent: 100 };
    const remainingHpEok = Math.max(numberValue(summary.maxHpEok) - summary.actualDamageEok, 0);
    const progressPercent = summary.maxHpEok > 0
      ? Math.max(0, Math.min(100, summary.actualDamageEok / summary.maxHpEok * 100))
      : 0;
    return { ...summary, remainingHpEok, progressPercent };
  });
}
